// # Filename: src/components/navbar/Navbar.jsx
import React, { useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { LuHouse, LuMenu, LuSpade } from "react-icons/lu";

import { useUI } from "../../context/uiContext";

const STATIC_TITLES = {
  "/games/sudoku": "Sudoku",
  "/games/connect-four/ai": "Connect Four",
  "/games/checkers/ai": "Checkers",
  "/games/poker/ai": "Poker",
  "/tournaments": "Tournaments",
  "/technical-paper": "Tech Overview",
  "/recruiter-demo": "Demo",
};

const DYNAMIC_TITLES = [
  [/^\/games\/ai\//, "Tic-Tac-Toe"],
  [/^\/games\/connect-four\//, "Connect Four"],
  [/^\/games\/checkers\//, "Checkers"],
  [/^\/games\/poker\//, "Poker"],
  [/^\/games\/\d+/, "Tic-Tac-Toe"],
  [/^\/lobby\//, "Lobby"],
];

function getPageTitle(pathname) {
  if (STATIC_TITLES[pathname] !== undefined) return STATIC_TITLES[pathname];
  for (const [pattern, title] of DYNAMIC_TITLES) {
    if (pattern.test(pathname)) return title;
  }
  return null;
}

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isSidebarOpen, setSidebarOpen } = useUI();

  const pageTitle = getPageTitle(location.pathname);
  const isHome = location.pathname === "/";

  const handleToggleSidebar = useCallback(() => {
    setSidebarOpen(!isSidebarOpen);
  }, [isSidebarOpen, setSidebarOpen]);

  return (
    <header className="sticky top-0 z-50 border-b border-border-soft bg-background-app-panel/90 backdrop-blur-xl">
      {/* thin cyan line at top */}
      <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-brand-cyan/40 to-transparent" />

      <div className="h-[60px] sm:h-[64px] px-3 sm:px-4 grid grid-cols-[minmax(0,1fr)_auto_minmax(44px,1fr)] items-center">

        {/* Left: home + wordmark */}
        <button
          type="button"
          onClick={() => navigate("/")}
          className={[
            "group flex w-fit max-w-full min-w-0 items-center gap-2 rounded-2xl border px-2 py-1.5 transition focus:outline-none",
            isHome ? "text-brand-cyan" : "text-text-secondary hover:text-brand-cyan",
            isHome ? "border-brand-cyan/25 bg-brand-cyan/10" : "border-transparent hover:border-border-soft hover:bg-surface",
          ].join(" ")}
          aria-label="Go to Game Hub"
          title="Game Hub"
        >
          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-xl border border-brand-cyan/25 bg-brand-cyan/10 text-brand-cyan shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]">
            {isHome ? <LuSpade size={17} /> : <LuHouse size={17} />}
          </span>
          <span className="flex min-w-0 flex-col items-start leading-none">
            <span className="hidden text-[9px] font-semibold uppercase tracking-[0.22em] text-text-faint sm:block">
              Game Hub
            </span>
            <span className="min-w-0 truncate text-[11px] font-semibold uppercase tracking-[0.16em] transition-colors duration-150 sm:text-[12px] sm:tracking-[0.2em]">
              {pageTitle ?? "Poker Lounge"}
            </span>
          </span>
        </button>

        {/* Center: reserved (Trinity is currently disabled) */}
        <div />

        {/* Right: hamburger (mobile only -- account now lives in the sidebar footer) */}
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={handleToggleSidebar}
            className="grid h-10 w-10 place-items-center rounded-xl border border-border-soft bg-surface text-text-muted transition hover:bg-surface-elevated hover:text-brand-cyan focus:outline-none lg:hidden"
            aria-label={isSidebarOpen ? "Close menu" : "Open menu"}
          >
            <LuMenu size={20} />
          </button>
        </div>

      </div>
    </header>
  );
}
